import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import { api } from "../api";
import { HiOutlineUser, HiOutlineCash, HiOutlineX } from "react-icons/hi";

function formatValor(valor) {
  let num =
    typeof valor === "string"
      ? parseFloat(valor.replace(",", "."))
      : Number(valor);

  if (isNaN(num)) num = 0;

  return "R$ " + num.toLocaleString("pt-BR", { minimumFractionDigits: 2 });
}

function formatData(data) {
  if (!data) return "";
  return data.slice(0, 10).split("-").reverse().join("/");
}

function normalizarChave(chave) {
  return String(chave || "").trim();
}

function usuarioTemPermissao(usuarioLogado, empresaId, chave) {
  const chaveNorm = normalizarChave(chave);

  if (!usuarioLogado || !chaveNorm) return false;

  const cargo = String(usuarioLogado?.cargo || "")
    .trim()
    .toLowerCase();
  const isAdmin = cargo === "admin" || cargo === "administrador";

  if (isAdmin) return true;

  if (usuarioLogado?.podeExcluirRecebimentoLead === true) return true;
  if (Number(usuarioLogado?.pode_excluir_recebimento_lead) === 1) return true;

  const permissoes = usuarioLogado?.permissoes;

  // Caso 1: permissoes por empresa -> { [empresaId]: { chave: true } }
  if (
    permissoes &&
    empresaId != null &&
    permissoes[String(empresaId)] &&
    typeof permissoes[String(empresaId)] === "object"
  ) {
    const blocoEmpresa = permissoes[String(empresaId)];

    if (blocoEmpresa[chaveNorm] === true) return true;
    if (Number(blocoEmpresa[chaveNorm]) === 1) return true;

    if (
      blocoEmpresa[chaveNorm] &&
      typeof blocoEmpresa[chaveNorm] === "object" &&
      (blocoEmpresa[chaveNorm].permitido === true ||
        Number(blocoEmpresa[chaveNorm].permitido) === 1)
    ) {
      return true;
    }
  }

  // Caso 2: permissoes flat -> { "receb_leads.excluir": true }
  if (permissoes && typeof permissoes === "object") {
    if (permissoes[chaveNorm] === true) return true;
    if (Number(permissoes[chaveNorm]) === 1) return true;
  }

  // Caso 3: array de strings
  if (Array.isArray(permissoes)) {
    if (permissoes.includes(chaveNorm)) return true;
  }

  return false;
}

export default function RecebimentoModal({
  isOpen,
  onClose,
  lead,
  onRecebimentoSalvo,
}) {
  if (!isOpen || !lead?.id) return null;

  let usuarioLogado = {};
  try {
    usuarioLogado = JSON.parse(localStorage.getItem("usuarioLogado") || "{}");
  } catch {
    usuarioLogado = {};
  }

  const empresaIdAtual = lead?.empresa_id || usuarioLogado?.empresa_id || null;

  const podeExcluirRecebimento = useMemo(() => {
    return usuarioTemPermissao(
      usuarioLogado,
      empresaIdAtual,
      "receb_leads.excluir",
    );
  }, [usuarioLogado, empresaIdAtual]);

  const [novoRecebimento, setNovoRecebimento] = useState({
    valor: "",
    data_recebimento: "",
  });
  const [recebimentos, setRecebimentos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [reloadFlag, setReloadFlag] = useState(0);

  useEffect(() => {
    if (!isOpen || !lead?.id) return;

    setNovoRecebimento({ valor: "", data_recebimento: "" });
    setRecebimentos([]);
    setLoading(true);

    const carregarRecebimentos = async () => {
      try {
        const res = await api.get(`/receb_leads/${lead.id}`);

        let arr;
        if (Array.isArray(res)) arr = res;
        else if (res && Array.isArray(res.data)) arr = res.data;
        else arr = [];

        setRecebimentos(arr);
      } catch {
        toast.error("Erro ao carregar recebimentos");
        setRecebimentos([]);
      }

      setLoading(false);
    };

    carregarRecebimentos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, lead?.id, reloadFlag]);

  const totalPago = recebimentos.reduce((soma, r) => {
    let num =
      typeof r.valor === "string"
        ? parseFloat(r.valor.toString().replace(",", "."))
        : Number(r.valor);

    if (isNaN(num)) num = 0;
    return soma + num;
  }, 0);

  const excluirRecebimento = async (id) => {
    if (!podeExcluirRecebimento) {
      toast.error("Você não tem permissão para excluir recebimentos.");
      return;
    }

    if (!window.confirm("Excluir este recebimento?")) return;

    try {
      await api.delete(`/receb_leads/${id}`);
      toast.success("Recebimento removido!");
      setReloadFlag((f) => f + 1);
      onRecebimentoSalvo?.();
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data || {};

      if (status === 403) {
        toast.error(
          data?.erro ||
            data?.error ||
            "Você não tem permissão para excluir recebimentos.",
        );
        return;
      }

      toast.error(data?.erro || data?.error || "Erro ao remover recebimento");
    }
  };

  const salvarRecebimento = async () => {
    if (!novoRecebimento.valor || !novoRecebimento.data_recebimento) {
      toast.warning("Preencha todos os campos!");
      return;
    }

    const valorFloat = parseFloat(
      String(novoRecebimento.valor).replace(",", "."),
    );

    if (isNaN(valorFloat) || valorFloat <= 0) {
      toast.warning("Valor inválido!");
      return;
    }

    setSalvando(true);

    try {
      await api.post("/receb_leads", {
        ...novoRecebimento,
        valor: valorFloat,
        lead_id: lead.id,
        empresa_id: usuarioLogado.empresa_id,
        usuario_id: usuarioLogado.id,
      });

      toast.success("Recebimento salvo com sucesso!");
      setNovoRecebimento({ valor: "", data_recebimento: "" });
      setReloadFlag((f) => f + 1);
      onRecebimentoSalvo?.();
    } catch {
      toast.error("Erro ao salvar recebimento");
    }

    setSalvando(false);
  };

  const recebimentosOrdenados = [...recebimentos].sort(
    (a, b) => new Date(b.data_recebimento) - new Date(a.data_recebimento),
  );

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="relative bg-white rounded-3xl w-full max-w-2xl shadow-2xl overflow-hidden border border-blue-100"
          initial={{ scale: 0.98, y: 30 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.98, y: 30 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-gradient-to-r from-blue-700 via-blue-500 to-blue-400 p-7 flex flex-col items-start md:items-center">
            <div className="flex items-center gap-3 w-full">
              <div className="rounded-full bg-white p-3 shadow-lg flex items-center justify-center">
                <HiOutlineCash className="text-blue-700 text-3xl" />
              </div>

              <div>
                <div className="text-lg md:text-2xl font-extrabold text-white tracking-tight mb-0.5">
                  Recebimentos
                </div>

                <div className="flex items-center gap-2">
                  <HiOutlineUser className="text-white opacity-80" />
                  <span className="text-base md:text-lg font-semibold text-white drop-shadow">
                    {lead?.nome_lead || lead?.nome}
                  </span>
                  <span className="ml-2 inline-block bg-green-100 text-green-700 text-xs font-bold px-2 py-0.5 rounded-full shadow">
                    Cliente Premium
                  </span>
                </div>
              </div>
            </div>

            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-white bg-blue-800 bg-opacity-40 rounded-full p-1.5 hover:bg-opacity-70 transition"
              title="Fechar"
              type="button"
            >
              <HiOutlineX className="text-xl" />
            </button>
          </div>

          <div className="p-6 pt-3 pb-2">
            <div className="mb-1 flex items-center gap-2">
              <span className="font-semibold text-gray-800">
                Histórico de pagamentos
              </span>
              <span className="ml-2 px-2 py-0.5 bg-blue-50 text-blue-600 text-xs rounded-full font-bold">
                {recebimentos.length} recebimento
                {recebimentos.length === 1 ? "" : "s"}
              </span>
            </div>

            {loading ? (
              <div className="text-gray-500 text-sm py-6">Carregando...</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-blue-50 text-blue-700 text-xs">
                      <th className="py-2 px-3">Data</th>
                      <th className="py-2 px-3">Valor</th>
                      <th className="py-2 px-3">Usuário</th>
                      <th className="py-2 px-3 text-center">Ação</th>
                    </tr>
                  </thead>

                  <tbody>
                    {recebimentosOrdenados.length === 0 ? (
                      <tr>
                        <td
                          colSpan={4}
                          className="text-gray-400 italic text-center py-4"
                        >
                          Nenhum lançamento ainda.
                        </td>
                      </tr>
                    ) : (
                      recebimentosOrdenados.map((r) => (
                        <tr
                          key={r.id}
                          className="border-b hover:bg-blue-50 transition"
                        >
                          <td className="py-2 px-3">
                            {formatData(r.data_recebimento)}
                          </td>
                          <td className="py-2 px-3 font-bold text-green-700">
                            {formatValor(r.valor)}
                          </td>
                          <td className="py-2 px-3 text-xs text-gray-600">
                            {r.nome_usuario}
                          </td>
                          <td className="py-2 px-3 text-center">
                            {podeExcluirRecebimento ? (
                              <button
                                onClick={() => excluirRecebimento(r.id)}
                                className="text-red-600 hover:bg-red-50 rounded-full p-2 transition"
                                title="Excluir recebimento"
                                type="button"
                              >
                                <HiOutlineX className="text-base" />
                              </button>
                            ) : (
                              <span className="text-xs text-gray-400">
                                Sem permissão
                              </span>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex items-center justify-end mt-4">
              <span className="inline-flex items-center bg-green-50 border border-green-200 px-4 py-2 rounded-xl font-bold text-green-700 text-lg shadow">
                <HiOutlineCash className="mr-2 text-green-500 text-xl" />
                Total já pago: {formatValor(totalPago)}
              </span>
            </div>
          </div>

          <div className="p-6 pt-2 pb-4">
            <div className="bg-white/90 rounded-xl shadow-lg border border-blue-100 px-4 py-5 flex flex-col gap-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-blue-700 mb-1">
                    Valor
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={novoRecebimento.valor}
                    onChange={(e) => {
                      setNovoRecebimento({
                        ...novoRecebimento,
                        valor: e.target.value,
                      });
                    }}
                    className="w-full px-3 py-2 border border-blue-200 rounded focus:ring-2 focus:ring-blue-300 outline-none bg-blue-50 text-lg font-bold"
                    placeholder="Ex: 10.00 ou 10,00"
                    disabled={salvando}
                  />
                </div>

                <div className="flex-1">
                  <label className="block text-sm font-medium text-blue-700 mb-1">
                    Data
                  </label>
                  <input
                    type="date"
                    value={novoRecebimento.data_recebimento}
                    onChange={(e) =>
                      setNovoRecebimento({
                        ...novoRecebimento,
                        data_recebimento: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-blue-200 rounded focus:ring-2 focus:ring-blue-300 outline-none bg-blue-50 text-lg"
                    disabled={salvando}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-2">
                <button
                  onClick={onClose}
                  className="text-sm font-semibold bg-gray-100 text-gray-700 rounded px-6 py-2 hover:bg-gray-200 transition"
                  disabled={salvando}
                  type="button"
                >
                  Fechar
                </button>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={salvarRecebimento}
                  className={`bg-gradient-to-r from-blue-600 to-blue-400 text-white font-bold rounded px-8 py-2 shadow-md hover:shadow-lg transition-all duration-150 ${
                    salvando ? "opacity-60 cursor-not-allowed" : ""
                  }`}
                  disabled={salvando}
                  type="button"
                >
                  {salvando ? "Salvando..." : "Salvar"}
                </motion.button>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
